/**
 * HeapWatchdog — JS heap watchdog for BrightSign Chromium instances.
 *
 * BrightSign players, especially the entry-tier LS series, run an embedded
 * Chromium with a tight heap budget (estimated ~128 MB on LS). Long-running
 * signage sessions gradually accumulate memory through blob URLs, detached
 * DOM nodes, and cached VAST creatives. If the renderer crosses its private
 * budget the tab is killed silently, the screen goes black, and the only
 * recovery is a hardware power cycle.
 *
 * This watchdog pre-empts that failure mode by:
 *
 *  1. **Polling `performance.memory`** every 30 seconds. Chromium-only API
 *     (not in the W3C spec) — guarded so other browsers no-op.
 *  2. **Early-warning reload at 80%** — when used heap crosses 80% of total
 *     heap, we wait 5 seconds (to allow V8 to GC naturally, which often
 *     pulls us back under the threshold) then re-check. If still over, we
 *     reload the page via `window.location.reload()`.
 *  3. **Nightly 3 AM local reload** — matches the scheduled-reload pattern
 *     on the Android CTV agent (see trillboard-ctv agent-core schedulers).
 *     This flushes any memory that slow leaks missed.
 *
 * All behaviour is wrapped in try/catch so a broken environment (for
 * example, locked-down iframe, sandbox restrictions) cannot break the host
 * application. The watchdog is a best-effort safety net — if it fails
 * silently the app continues running.
 *
 * Test hooks:
 *  - Pass a `now()` function to the constructor to control the clock.
 *  - Pass a `reload()` function to intercept the reload call.
 *  - Pass a `memory()` function to fake the Chromium heap snapshot.
 *  - `start()` / `stop()` toggle the polling interval.
 */

/* eslint-disable no-console */

const DEFAULT_POLL_INTERVAL_MS = 30 * 1000;
const DEFAULT_GRACE_PERIOD_MS = 5 * 1000;
const DEFAULT_THRESHOLD_RATIO = 0.8;
const NIGHTLY_RELOAD_HOUR = 3; // 3 AM local time

function safePostBsMessage(payload) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.BSMessagePort &&
      typeof window.BSMessagePort.PostBSMessage === 'function'
    ) {
      window.BSMessagePort.PostBSMessage(JSON.stringify(payload));
    }
  } catch (err) {
    // swallow — telemetry must never break the app
  }
}

function defaultNow() {
  return Date.now();
}

function defaultReload() {
  try {
    if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
      window.location.reload();
    }
  } catch (err) {
    // If reload is unavailable there is nothing more we can do.
  }
}

/**
 * Read the Chromium heap snapshot. Returns null when the API is absent —
 * safari, firefox, older chromium, or a locked-down sandbox.
 */
function defaultMemorySnapshot() {
  try {
    if (typeof performance === 'undefined') return null;
    const mem = performance.memory;
    if (!mem) return null;
    const used = Number(mem.usedJSHeapSize);
    const total = Number(mem.totalJSHeapSize);
    const limit = Number(mem.jsHeapSizeLimit);
    if (!Number.isFinite(used) || !Number.isFinite(total)) return null;
    return {
      usedJSHeapSize: used,
      totalJSHeapSize: total,
      jsHeapSizeLimit: Number.isFinite(limit) ? limit : total,
    };
  } catch (err) {
    return null;
  }
}

class HeapWatchdog {
  /**
   * @param {object} [options]
   * @param {number} [options.pollIntervalMs=30000] poll cadence
   * @param {number} [options.gracePeriodMs=5000]   grace period before reload
   * @param {number} [options.thresholdRatio=0.8]   fraction of totalJSHeapSize
   * @param {number} [options.nightlyReloadHour=3]  local hour for nightly reload
   * @param {function} [options.now]                clock injection
   * @param {function} [options.reload]             reload injection
   * @param {function} [options.memory]             memory snapshot injection
   * @param {function} [options.logger]             optional structured logger
   */
  constructor(options) {
    const opts = options || {};
    this.pollIntervalMs = opts.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;
    this.gracePeriodMs = opts.gracePeriodMs || DEFAULT_GRACE_PERIOD_MS;
    this.thresholdRatio = typeof opts.thresholdRatio === 'number' ? opts.thresholdRatio : DEFAULT_THRESHOLD_RATIO;
    this.nightlyReloadHour = typeof opts.nightlyReloadHour === 'number' ? opts.nightlyReloadHour : NIGHTLY_RELOAD_HOUR;
    this.now = opts.now || defaultNow;
    this.reload = opts.reload || defaultReload;
    this.memory = opts.memory || defaultMemorySnapshot;
    this.logger = opts.logger || null;

    this.pollTimer = null;
    this.graceTimer = null;
    this.nightlyTimer = null;
    this.started = false;
    this.reloadScheduled = false;
    this.lastSnapshot = null;

    // Public counters — test hooks can read these to assert behaviour
    // without stubbing timer internals.
    this.pollCount = 0;
    this.warningCount = 0;
    this.reloadCount = 0;
  }

  /**
   * Boot the watchdog. Safe to call repeatedly — second call is a no-op.
   * Schedules the first poll to run immediately (useful for tests) followed
   * by a recurring interval.
   */
  start() {
    if (this.started) return;
    this.started = true;

    // Fire one poll immediately so we have a baseline snapshot before the
    // first interval tick. Wrapped in try/catch so failures can't cascade.
    try {
      this.poll();
    } catch (err) {
      this._log('warn', 'HeapWatchdog initial poll failed', { error: err && err.message });
    }

    this.pollTimer = setInterval(() => {
      try {
        this.poll();
      } catch (err) {
        this._log('warn', 'HeapWatchdog poll failed', { error: err && err.message });
      }
    }, this.pollIntervalMs);

    this._scheduleNightlyReload();
  }

  /**
   * Stop the watchdog. Cancels all pending timers. Safe to call repeatedly.
   */
  stop() {
    this.started = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
    if (this.nightlyTimer) {
      clearTimeout(this.nightlyTimer);
      this.nightlyTimer = null;
    }
    this.reloadScheduled = false;
  }

  /**
   * Perform a single heap check. Public so tests can invoke directly
   * without waiting on setInterval. Returns the fraction used (0 when the
   * API is unavailable).
   */
  poll() {
    this.pollCount += 1;
    const snapshot = this.memory();
    if (!snapshot) {
      // performance.memory not available — log once on the first poll so we
      // can see it in DWS, then stay silent.
      if (this.pollCount === 1) {
        this._log('info', 'HeapWatchdog performance.memory unavailable, polling is a no-op');
      }
      return 0;
    }

    this.lastSnapshot = snapshot;
    const ratio = snapshot.totalJSHeapSize > 0
      ? snapshot.usedJSHeapSize / snapshot.totalJSHeapSize
      : 0;

    safePostBsMessage({
      type: 'heap',
      usedMB: Math.round(snapshot.usedJSHeapSize / 1024 / 1024),
      totalMB: Math.round(snapshot.totalJSHeapSize / 1024 / 1024),
      limitMB: Math.round((snapshot.jsHeapSizeLimit || snapshot.totalJSHeapSize) / 1024 / 1024),
      ratio: Number(ratio.toFixed(3)),
    });

    if (ratio >= this.thresholdRatio) {
      this._handleThresholdCrossed(snapshot, ratio);
    }

    return ratio;
  }

  /**
   * Runs when the first poll returns a ratio over the threshold. Schedules
   * a grace-period re-check before reloading — the grace period is
   * essential because V8's incremental marker often reclaims megabytes in
   * the 5s between polls, so a hot reload on the very first warning would
   * cause needless service interruption.
   */
  _handleThresholdCrossed(snapshot, ratio) {
    this.warningCount += 1;
    this._log('warn', 'HeapWatchdog heap above threshold', {
      usedMB: Math.round(snapshot.usedJSHeapSize / 1024 / 1024),
      totalMB: Math.round(snapshot.totalJSHeapSize / 1024 / 1024),
      ratio: Number(ratio.toFixed(3)),
      thresholdRatio: this.thresholdRatio,
    });

    safePostBsMessage({
      type: 'heap_warning',
      usedMB: Math.round(snapshot.usedJSHeapSize / 1024 / 1024),
      totalMB: Math.round(snapshot.totalJSHeapSize / 1024 / 1024),
      ratio: Number(ratio.toFixed(3)),
    });

    if (this.reloadScheduled) return;
    this.reloadScheduled = true;

    // Nudge V8 to GC immediately if the --expose-gc flag is on. Harmless
    // when not available.
    try {
      if (typeof window !== 'undefined' && typeof window.gc === 'function') {
        window.gc();
      }
    } catch (_) { /* ignore */ }

    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      let stillOver = true;
      try {
        const recheck = this.memory();
        if (recheck && recheck.totalJSHeapSize > 0) {
          const recheckRatio = recheck.usedJSHeapSize / recheck.totalJSHeapSize;
          stillOver = recheckRatio >= this.thresholdRatio;
          this.lastSnapshot = recheck;
          this._log('info', 'HeapWatchdog grace period re-check', {
            ratio: Number(recheckRatio.toFixed(3)),
            stillOver,
          });
        }
      } catch (err) {
        this._log('warn', 'HeapWatchdog grace period recheck failed', { error: err && err.message });
      }

      if (stillOver) {
        this._triggerReload('heap_pressure');
      } else {
        // GC rescued us — reset so the next warning can trip a new cycle.
        this.reloadScheduled = false;
      }
    }, this.gracePeriodMs);
  }

  /**
   * Schedule a reload at 3 AM local time. Matches the Android CTV
   * agent-core pattern where each device performs a nightly process
   * restart to flush any leaks that incremental GC missed.
   */
  _scheduleNightlyReload() {
    if (this.nightlyTimer) return;
    const delay = this._computeNightlyDelay();
    this.nightlyTimer = setTimeout(() => {
      this.nightlyTimer = null;
      this._triggerReload('nightly_refresh');
    }, delay);
  }

  /**
   * Compute the milliseconds until the next occurrence of
   * `nightlyReloadHour` local time. Extracted so tests can verify the
   * scheduling math directly.
   */
  _computeNightlyDelay() {
    try {
      const nowMs = this.now();
      const d = new Date(nowMs);
      const target = new Date(nowMs);
      target.setHours(this.nightlyReloadHour, 0, 0, 0);
      if (target.getTime() <= d.getTime()) {
        // Already past the hour today — schedule for tomorrow.
        target.setDate(target.getDate() + 1);
      }
      const delay = target.getTime() - nowMs;
      // Clamp to a sane minimum so malformed clocks don't spin-loop.
      return delay < 1000 ? 1000 : delay;
    } catch (err) {
      // If Date is broken for any reason, fall back to 24h.
      return 24 * 60 * 60 * 1000;
    }
  }

  /**
   * Fire the reload. Uses the injected reload function so tests can
   * observe without actually navigating jsdom.
   */
  _triggerReload(reason) {
    this.reloadCount += 1;
    this._log('warn', 'HeapWatchdog triggering reload', { reason });
    safePostBsMessage({ type: 'heap_reload', reason });
    try {
      this.reload(reason);
    } catch (err) {
      this._log('error', 'HeapWatchdog reload threw', { error: err && err.message });
    }
  }

  /**
   * Internal logger — uses the injected logger if present, otherwise falls
   * back to console at the matching level. Never throws.
   */
  _log(level, message, fields) {
    try {
      if (this.logger && typeof this.logger[level] === 'function') {
        this.logger[level](message, fields || {});
        return;
      }
      if (typeof console === 'undefined') return;
      const fn = console[level] || console.log;
      if (fields) {
        fn.call(console, '[HeapWatchdog]', message, fields);
      } else {
        fn.call(console, '[HeapWatchdog]', message);
      }
    } catch (_) {
      // never let logging break the watchdog
    }
  }
}

/**
 * Install a singleton watchdog on `window.__TRILL_HEAP_WATCHDOG__` and
 * start it. Idempotent — repeated calls return the existing instance.
 *
 * `bootstrap.js` calls this for every BrightSign tier (LS / HD / XD /
 * XT / XC) because the memory-pressure failure mode is common to every
 * Chromium instance, not just LS.
 */
function installHeapWatchdog(options) {
  if (typeof window === 'undefined') return null;
  if (window.__TRILL_HEAP_WATCHDOG__) {
    return window.__TRILL_HEAP_WATCHDOG__;
  }
  const watchdog = new HeapWatchdog(options);
  window.__TRILL_HEAP_WATCHDOG__ = watchdog;
  watchdog.start();
  return watchdog;
}

export {
  HeapWatchdog,
  installHeapWatchdog,
  defaultMemorySnapshot,
  DEFAULT_POLL_INTERVAL_MS,
  DEFAULT_GRACE_PERIOD_MS,
  DEFAULT_THRESHOLD_RATIO,
  NIGHTLY_RELOAD_HOUR,
};
